<template>
    <v-dialog
        :value="isOpen"
        max-width="1300px"
        height="1500px"
        transition="slide-y-transition"
        eager
        @keydown.esc="onClose"
        @click:outside="onClose"
    >
        <CodeReaderDialog
            :is-open="codeReaderDialog"
            :settings="codeReaderSettings"
            @code-stream-start="showCodeReaderDialog"
            @code-stream-stop="hideCodeReaderDialog"
            @code-stream-handle="handleQrJSON"
        />
        <v-card>
            <v-card-title>
                <h1 class="text-subtitle-1">
                    Zamówienia z odbiorem osobistym
                </h1>
            </v-card-title>
            <v-card-text>
                <v-row>
                    <v-col cols="12">
                        <v-container fluid class="pa-0">
                            <v-row dense justify="end">
                                <v-col cols="12" sm="4" lg="3">
                                    <v-text-field
                                        v-model="filters.customerName"
                                        label="Klient"
                                        single-line
                                        clearable
                                        hide-details
                                        solo
                                        :disabled="action === 'updatePersonalPickup' && personalPickupOrdersListPassed.length > 0"
                                        @keyup.enter="changeFilter"
                                        @click:append="changeFilter"
                                        @click:clear="clearFilter('customerName')"
                                    />
                                </v-col>
                                <v-col cols="12" sm="4" lg="3">
                                    <v-text-field
                                        v-model="filters.shopOrderNumber"
                                        label="Numer zamówienia"
                                        single-line
                                        clearable
                                        hide-details
                                        solo
                                        @keyup.enter="changeFilter"
                                        @click:append="changeFilter"
                                        @click:clear="clearFilter('shopOrderNumber')"
                                    />
                                </v-col>
                                <v-col cols="auto">
                                    <v-btn
                                        v-if="hasCamera"
                                        fab
                                        x-small
                                        color="primary"
                                        class="hidden-md-and-up elevation-1 mx-3"
                                        data-sentry-label="Otwórz czytnik kodów"
                                        @click="showCodeReaderDialog({
                                            expectedQrTypeCodes: ['KZ', 'KP'],
                                            origin: null
                                        })"
                                    >
                                        <v-icon>mdi-camera</v-icon>
                                    </v-btn>
                                </v-col>
                            </v-row>
                        </v-container>
                    </v-col>
                </v-row>
                <v-row>
                    <v-col cols="12">
                        <v-card>
                            <v-data-table
                                v-model="personalPickupsOrdersSelected"
                                :headers="headers"
                                :items="personalPickupsOrders"
                                :loading="personalPickupsOrdersLoading"
                                :footer-props="footerProps"
                                :server-items-length="pagination.itemsLength"
                                :page.sync="pagination.page"
                                show-select
                                item-key="id"
                                :mobile-breakpoint="0"
                                @item-selected="updateFilters"
                                @pagination="updatePagination"
                            >
                                <template #item.deliveryMethod="{item}">
                                    <template v-if="item.deliveryMethod == 'TRANS'">
                                        <v-icon color="warning" small>
                                            mdi-truck-delivery-outline
                                        </v-icon>
                                    </template>
                                </template>
                                <template #item.customer="{item}">
                                    <template v-if="item.customer">
                                        {{ item.customer }}
                                    </template>
                                    <template v-else>
                                        -
                                    </template>
                                </template>
                                <template #item.shopOrderNumer="{item}">
                                    <template v-if="item.shopOrderNumer">
                                        {{ item.shopOrderNumer }}
                                    </template>
                                    <template v-else>
                                        -
                                    </template>
                                </template>
                                <template #item.productionNumber="{item}">
                                    <template v-if="item.productionNumber">
                                        {{ item.productionNumber }}
                                    </template>
                                    <template v-else>
                                        -
                                    </template>
                                </template>
                                <template #item.firstProductName="{item}">
                                    {{ item.firstProductName }}
                                </template>
                                <template #item.status="{item}">
                                    <template v-if="item.status">
                                        {{ item.status }}
                                    </template>
                                </template>
                                <template #item.quantity="{item}">
                                    {{ item.quantity }}
                                </template>
                                <template #item.quantityLeft="{item}">
                                    <template v-if="item.quantityLeft">
                                        {{ item.quantityLeft }}
                                    </template>
                                    <template v-else>
                                        -
                                    </template>
                                </template>
                            </v-data-table>
                        </v-card>
                    </v-col>
                </v-row>
            </v-card-text>
            <v-card-actions>
                <v-btn
                    color="error"
                    text
                    data-sentry-label="Anuluj"
                    @click="onClose()"
                >
                    Anuluj
                </v-btn>
                <v-spacer />
                <v-btn
                    color="success"
                    text
                    data-sentry-label="Dodaj"
                    :loading="actionLoading"
                    @click="onSave()"
                >
                    Dodaj
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
import CodeReaderDialog from '@/components/common/CodeReaderDialog'
import { errorHandler } from '@/utils/errorHandler'
import filters from '@/mixins/dataTableFilters'
import pagination from '@/mixins/dataTablePagination'
import tooltipDelay from '@/mixins/tooltip'
import dateFormatter from '@/mixins/dateFormatter'
import customFilters from '@/mixins/customFilters'
import { isEmpty } from 'lodash'
import decodeQRcode from '@/mixins/decodeQRcode'

export default {
    name: 'PersonalPickupOrdersDialog',
    components: {
        CodeReaderDialog
    },
    mixins: [
        pagination,
        tooltipDelay,
        filters,
        dateFormatter,
        customFilters,
        decodeQRcode
    ],
    props: {
        isOpen: {
            type: Boolean
        },
        action: {
            type: String,
            default: null
        },
        personalPickupOrdersListPassed: {
            type: Array,
            default: () => []
        },
        personalPickupData: {
            type: Object,
            default: null
        },
    },
    data() {
        return {
            codeReaderSettings: {
                expectedQrTypeCodes: null,
                origin: null
            },
            personalPickupsOrdersSelected: [],
            actionLoading: false,
            codeReaderDialog: false,
            personalPickup: null,
            headers: [
                {
                    text: '',
                    value: 'deliveryMethod',
                    sortable: false,
                    width: 10
                },
                {
                    text: 'Klient',
                    value: 'customer',
                    sortable: false,
                    width: 200
                },
                {
                    text: 'Numer zamówienia klienta',
                    value: 'shopOrderNumber',
                    sortable: false,
                },
                {
                    text: 'Numer zamówienia',
                    value: 'productionNumber',
                    sortable: false,
                },
                {
                    text: 'Produkt',
                    value: 'firstProductName',
                    sortable: false,
                },
                {
                    text: 'Status zamówienia',
                    value: 'status',
                    sortable: false,
                },
                {
                    text: 'Nakład',
                    value: 'quantity',
                    sortable: false,
                },
                {
                    text: 'Pozostało',
                    value: 'quantityLeft',
                    sortable: false,
                },
            ]
        }
    },
    computed: {
        hasCamera() {
            return this.$store.state.common.hasCamera
        },
        personalPickupsOrders() {
            return this.$store.state.shipments.personalPickups.personalPickups.personalPickupsOrders
        },
        personalPickupsOrdersLoading() {
            return this.$store.state.shipments.personalPickups.personalPickups.personalPickupsOrdersLoading
        },
        savedFilters() {
            return this.$store.state.shipments.personalPickups.personalPickups.filters
        },
        filterQuery() {
            const filters = this.filters
            let query = ''

            if (this.action === 'updatePersonalPickup' && this.personalPickupOrdersListPassed.length > 0 && !isEmpty(this.personalPickup)) {
                filters['customerName'] = ''
                filters['customerId'] = this.personalPickupOrdersListPassed[0].customer ? this.personalPickupOrdersListPassed[0].customer.id : this.personalPickup.customerId
            }
            
            if (!isEmpty(filters['shopOrderNumber'])) {
                let shop_order_number = filters['shopOrderNumber'].split('_')
                
                if (shop_order_number.length > 1) {
                    filters['shopOrderNumber'] = shop_order_number[0] + '_' + shop_order_number[1]
                }

            }
            for (let item in filters) {
                if (filters[item] != null && filters[item] != '') {
                    query = query.concat('&', `${item}=${filters[item]}`)
                }

            }

            return query
        }
    },
    watch: {
        isOpen(boolean) {
            if (boolean) {
                if (this.personalPickupOrdersListPassed.length === 0) this.filters['customerId'] = ''
                this.personalPickup = JSON.parse(JSON.stringify(this.personalPickupData))
                this.getPersonalPickupsOrders(true)
            }
        }
    },
    created() {
        document.addEventListener('keydown', this.QRtoJSON)
    },
    destroyed() {
        document.removeEventListener('keydown', this.QRtoJSON)
    },
    methods: {
        showCodeReaderDialog(data) {
            this.codeReaderSettings = data
            this.codeReaderDialog = true
        },
        hideCodeReaderDialog() {
            this.codeReaderDialog = false
        },
        handleQrJSON({ data }) {
            if (data.type === 'KZ' || data.type === 'KP') {
                this.scannerEnterFallback(() => {
                    this.filters.shopOrderNumber = data.order
                    this.getPersonalPickupsOrders()
                })
            }
            else {
                this.$store.dispatch('showNotification', {
                    type: 'error',
                    message: this.$i18n.t('reports.messages.unsupportedQrCode')
                })
            }
        },
        getPersonalPickupsOrders(firstCall = false) {
            this.$store.dispatch('shipments/personalPickups/getPersonalPickupsOrders', {
                paginate: this.paginate(),
                query: this.filterQuery
            })
                .then(response => {
                    this.handlePaginationResponse(response)
                    if (firstCall) {
                        this.personalPickupOrdersListPassed.forEach(item => {
                            const index = this.personalPickupsOrders.findIndex(order => order.id === item.order)

                            if (index > -1) this.personalPickupsOrdersSelected.push(this.personalPickupsOrders[index])
                        })
                    }
                })
                .catch(error => {
                    errorHandler(error)
                })
        },
        onSave() {
            this.$emit('on-save', this.personalPickupsOrdersSelected)
            this.onClose()
        },
        onClose() {
            this.$store.dispatch('shipments/personalPickups/clearPersonalPickupsOrdersFilters')
            setTimeout(() => {
                this.actionLoading = false
                this.personalPickupsOrdersSelected = []
            }, 500)
            this.$emit('on-close')
        },
        updateFilters(seletced) {
            if (this.action === 'updatePersonalPickup') {
                if (seletced.value) {
                    this.filters['customerName'] = ''
                    this.filters['customerId'] = seletced.item.customer.id
                }
                if (this.personalPickupOrdersListPassed.length > 0 || (!seletced.value && this.personalPickupsOrdersSelected.length === 1)) {
                    this.filters['customerId'] = ''
                }
                this.getPersonalPickupsOrders()
            }
        },
        changeFilter() {
            this.$store.dispatch('shipments/personalPickups/setPersonalPickupsOrdersFilters', this.filters)
            this.resetPagination()
            this.getPersonalPickupsOrders()
        },
        updatePagination(pagination) {
            this.pagination = pagination
            this.evaluateSavedPagination(this.$options.name)
            if (pagination.pageCount !== 0 && !this.paginationSettings.paginationPending) {
                this.getPersonalPickupsOrders()
            }
            this.clearPaginationPending()
        },
    },
}
</script>
